import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useStripe } from "@stripe/react-stripe-js";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { isApiError, isTerminalPaymentAttemptError } from "@bw/shared/api";
import { useStripeIntentAuthentication } from "@bw/shared/hooks";
import { formatMoney, renderMarkdown } from "@bw/shared/utils";
import type {
  GuardianConsentAcceptance,
  GuardianConsentConfirmResponse,
  StripeFrontendConfigResponse,
} from "@bw/shared/types/generated";
import {
  useConfirmGuardianConsent,
  useGuardianConsentPreview,
} from "../hooks/useConfirmGuardianConsent";
import { MaterialConnectedElementsProvider } from "../../../common/components/payment";

/**
 * GuardianConsentPage (#1684 / #1692) — the PUBLIC parental-consent confirmation
 * page, route `/guardian-consent#token=…`. A held MINOR's parent/guardian clicks
 * the signed link in the email they received and confirms consent here. The
 * guardian is NOT necessarily a logged-in BW user, so this page renders OUTSIDE
 * the AuthGuard (chrome-less, like /terms + /privacy) and the signed token in the
 * URL fragment is the ONLY credential. A fragment is never sent in the initial
 * HTTP request; this page reads it once and immediately scrubs the address bar.
 *
 * THIN presentation — the backend owns ALL consent/age/money logic. The page only:
 *   1. reads the token from #token= and removes it from browser history,
 *   2. POSTs it in a JSON body to the preview/confirm endpoints on a single
 *      "Confirm consent" click (never auto-submits a charge), and
 *   3. renders the server's truth:
 *        - 200 DrawEntryResponse → "Consent confirmed" using the returned child +
 *          event; for a PAID entry the saved card was charged off-session, shown
 *          honestly from the entry's own payment_status + amount_paid.
 *        - 402 PAYMENT_AUTHENTICATION_REQUIRED → initialize connected-account
 *          Stripe.js from the structured routing config, confirm the existing
 *          PaymentIntent, then retry the SAME signed token.
 *        - 402 PAYMENT_NEEDS_ATTENTION → the off-session charge DECLINED; an honest
 *          "payment couldn't be completed — please try again" + a retry of the
 *          SAME confirm (#1692 off-session-decline surface).
 *        - 400 (token invalid / expired / tampered) → an "invalid or expired link"
 *          terminal screen pointing the guardian at the organizer to re-send (#1690).
 *
 * The page fabricates NO amount/fee/eligibility and decides nothing about who is a
 * minor or what consent requires — it forwards the token and shows what comes back.
 */

type ErrorKind =
  | "authentication"
  | "payment"
  | "invalid"
  | "changed"
  | "generic";

interface GuardianPaymentAuthentication {
  clientSecret: string;
  config: StripeFrontendConfigResponse;
}

function getGuardianPaymentAuthentication(
  error: unknown,
): GuardianPaymentAuthentication | null {
  if (
    !isApiError(error) ||
    error.status !== 402 ||
    error.code !== "PAYMENT_AUTHENTICATION_REQUIRED"
  ) {
    return null;
  }
  const clientSecret = error.details?.client_secret;
  const publishableKey = error.details?.publishable_key;
  const connectedAccountId = error.details?.connected_account_id;
  const mode = error.details?.mode;
  if (
    typeof clientSecret !== "string" ||
    !clientSecret ||
    typeof publishableKey !== "string" ||
    !publishableKey ||
    typeof connectedAccountId !== "string" ||
    !connectedAccountId ||
    typeof mode !== "string" ||
    !mode
  ) {
    return null;
  }
  return {
    clientSecret,
    config: {
      publishable_key: publishableKey,
      connected_account_id: connectedAccountId,
      stripe_account_id: connectedAccountId,
      mode,
    },
  };
}

/** Branch the confirm error by intent. PaymentIntent authentication and ordinary
 *  declines have distinct recovery paths; 400 is an unusable token (terminal). */
function classifyError(error: unknown): ErrorKind | null {
  if (!error) return null;
  if (isApiError(error)) {
    if (error.code === "PAYMENT_AUTHENTICATION_REQUIRED") {
      return "authentication";
    }
    if (error.code === "PAYMENT_NEEDS_ATTENTION") {
      return "payment";
    }
    if (error.status === 400) return "invalid";
    if (error.status === 409) return "changed";
  }
  return "generic";
}

function tokenFromGuardianFragment(fragment: string): string | null {
  const token = new URLSearchParams(fragment.replace(/^#/, "")).get("token");
  return token?.trim() || null;
}

export function GuardianConsentPage() {
  const location = useLocation();
  const [token] = useState(() => tokenFromGuardianFragment(location.hash));
  const preview = useGuardianConsentPreview(token);
  const confirm = useConfirmGuardianConsent();
  const [acceptedDocumentIds, setAcceptedDocumentIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [submittedAcceptances, setSubmittedAcceptances] = useState<
    GuardianConsentAcceptance[] | null
  >(null);
  const rawErrorKind = classifyError(confirm.error);
  const detectedPaymentAuthentication = useMemo(
    () => getGuardianPaymentAuthentication(confirm.error),
    [confirm.error],
  );
  const [activePaymentAuthentication, setActivePaymentAuthentication] =
    useState<GuardianPaymentAuthentication | null>(null);
  useEffect(() => {
    if (!token || !location.hash) return;
    // The token came from the email fragment. Keep it only in component memory
    // for this tab, never in the visible URL/history/referrer state.
    window.history.replaceState(null, "", location.pathname);
  }, [location.hash, location.pathname, token]);
  useEffect(() => {
    if (detectedPaymentAuthentication) {
      setActivePaymentAuthentication((current) =>
        current?.clientSecret === detectedPaymentAuthentication.clientSecret
          ? current
          : detectedPaymentAuthentication,
      );
      return;
    }
    if (isTerminalPaymentAttemptError(confirm.error)) {
      setActivePaymentAuthentication(null);
    }
  }, [confirm.error, detectedPaymentAuthentication]);
  const paymentAuthentication =
    detectedPaymentAuthentication ??
    (rawErrorKind === null || rawErrorKind === "generic"
      ? activePaymentAuthentication
      : null);
  const errorKind = paymentAuthentication ? "authentication" : rawErrorKind;
  const previewInvalid =
    preview.error && isApiError(preview.error) && preview.error.status === 400;
  const previewChanged =
    preview.error && isApiError(preview.error) && preview.error.status === 409;

  // Terminal "unusable link" screen: a missing token, or the backend rejecting
  // the token as invalid/expired. No Confirm action — the guardian must get a
  // fresh link from the organizer.
  if (!token || previewInvalid || errorKind === "invalid") {
    return <InvalidLinkScreen />;
  }

  if (previewChanged || errorKind === "changed") {
    return <RegistrationChangedScreen />;
  }

  if (preview.isPending) {
    return (
      <ConsentShell>
        <Typography component="h1" variant="h4">
          Parental consent
        </Typography>
        <Typography color="text.secondary" role="status">
          Loading consent details…
        </Typography>
      </ConsentShell>
    );
  }

  if (preview.isError || !preview.data) {
    return (
      <ConsentShell>
        <Typography component="h1" variant="h4">
          Parental consent
        </Typography>
        <Alert severity="error" role="alert">
          We couldn&apos;t load the consent details.
        </Alert>
        <Box>
          <Button
            type="button"
            variant="contained"
            onClick={() => void preview.refetch()}
            disabled={preview.isFetching}
          >
            {preview.isFetching ? "Loading…" : "Try again"}
          </Button>
        </Box>
      </ConsentShell>
    );
  }

  if (preview.data.already_confirmed) {
    return <AlreadyConfirmedScreen />;
  }

  // Terminal success screen — consent recorded, the entry advanced (and, when
  // paid, the saved card charged). Renders the server's returned context.
  if (confirm.isSuccess && confirm.data) {
    return <ConfirmedScreen entry={confirm.data} />;
  }

  const legalDocuments = preview.data.legal_documents ?? [];
  const allDocumentsAccepted = legalDocuments.every((document) =>
    acceptedDocumentIds.has(document.document_id),
  );
  const currentAcceptances = legalDocuments
    .filter((document) => acceptedDocumentIds.has(document.document_id))
    .map((document) => ({
      document_id: document.document_id,
      version: document.version,
    }));
  const retryAcceptances = submittedAcceptances ?? currentAcceptances;
  const controlsFrozen = submittedAcceptances !== null || confirm.isPending;
  const playerName = preview.data.player_display_name || "Not available";
  const eventName = preview.data.event_name || "Not available";
  const paymentRequired = preview.data.payment_required;
  const amountDue = formatMoney(preview.data.amount_due, preview.data.currency);

  const submitConsent = () => {
    if (!allDocumentsAccepted) return;
    const acceptances = currentAcceptances;
    setSubmittedAcceptances(acceptances);
    confirm.mutate({ token, acceptances });
  };

  return (
    <ConsentShell>
      <Typography component="h1" variant="h4">
        Parental consent
      </Typography>
      <Typography color="text.secondary">
        Review this tournament entry before giving consent.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          component="dl"
          aria-label="Entry details"
          sx={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: 1,
            m: 0,
          }}
        >
          <Typography component="dt" color="text.secondary">
            Player
          </Typography>
          <Typography component="dd" sx={{ m: 0, fontWeight: 700 }}>
            {playerName}
          </Typography>
          <Typography component="dt" color="text.secondary">
            Event
          </Typography>
          <Typography component="dd" sx={{ m: 0, fontWeight: 700 }}>
            {eventName}
          </Typography>
          <Typography component="dt" color="text.secondary">
            {paymentRequired ? "Charge on confirmation" : "Entry fee"}
          </Typography>
          <Typography component="dd" sx={{ m: 0, fontWeight: 700 }}>
            {paymentRequired ? amountDue : "No charge"}
          </Typography>
        </Box>
      </Paper>

      <Typography variant="body2" color="text.secondary">
        {paymentRequired
          ? "Confirming consent charges the saved card " + amountDue + "."
          : "Confirming consent does not charge a card."}{" "}
        No account is needed.
      </Typography>

      {legalDocuments.map((document) => (
        <Paper
          component="section"
          variant="outlined"
          key={`${document.document_id}:${document.version}`}
          sx={{ p: 2 }}
        >
          <Typography component="h2" variant="h6">
            {document.title}
          </Typography>
          <Box sx={{ color: "text.secondary", typography: "body2", my: 1 }}>
            {renderMarkdown(document.body)}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedDocumentIds.has(document.document_id)}
                disabled={controlsFrozen}
                onChange={(event) => {
                  setAcceptedDocumentIds((current) => {
                    const next = new Set(current);
                    if (event.target.checked) next.add(document.document_id);
                    else next.delete(document.document_id);
                    return next;
                  });
                }}
              />
            }
            label={`I accept ${document.title} as this child's guardian.`}
          />
        </Paper>
      ))}

      {errorKind === "authentication" && paymentAuthentication ? (
        <MaterialConnectedElementsProvider
          config={paymentAuthentication.config}
        >
          <GuardianPaymentScaChallenge
            clientSecret={paymentAuthentication.clientSecret}
            onAuthenticated={async (authenticatedClientSecret) => {
              if (
                paymentAuthentication.clientSecret !== authenticatedClientSecret
              ) {
                return;
              }
              await confirm.mutateAsync({
                token,
                acceptances: retryAcceptances,
              });
            }}
          />
        </MaterialConnectedElementsProvider>
      ) : null}
      {errorKind === "authentication" && !paymentAuthentication ? (
        <Alert severity="error" role="alert">
          Payment verification is unavailable. Please contact the event
          organizer.
        </Alert>
      ) : null}
      {errorKind === "payment" ? (
        <Alert severity="error" role="alert">
          The payment couldn&apos;t be completed — please try again.
        </Alert>
      ) : null}
      {errorKind === "generic" ? (
        <Alert severity="error" role="alert">
          We couldn&apos;t confirm consent just now. Please try again.
        </Alert>
      ) : null}

      {errorKind !== "authentication" ? (
        <Box>
          <Button
            type="button"
            variant="contained"
            onClick={
              errorKind === "payment" || errorKind === "generic"
                ? () =>
                    confirm.mutate({
                      token,
                      acceptances: retryAcceptances,
                    })
                : submitConsent
            }
            disabled={confirm.isPending || !allDocumentsAccepted}
            aria-busy={confirm.isPending || undefined}
          >
            {confirm.isPending
              ? "Confirming…"
              : errorKind === "payment" || errorKind === "generic"
                ? "Try again"
                : paymentRequired
                  ? "Approve and pay " + amountDue
                  : "Confirm consent"}
          </Button>
        </Box>
      ) : null}
    </ConsentShell>
  );
}

function GuardianPaymentScaChallenge({
  clientSecret,
  onAuthenticated,
}: {
  clientSecret: string;
  onAuthenticated: (authenticatedClientSecret: string) => Promise<void>;
}) {
  const stripe = useStripe();
  const confirmIntent = useCallback(async (): Promise<boolean> => {
    if (!stripe) return false;
    const result = await stripe.confirmCardPayment(clientSecret);
    return !result.error && result.paymentIntent?.status === "succeeded";
  }, [clientSecret, stripe]);
  const { phase, busy, authenticate } = useStripeIntentAuthentication({
    clientSecret,
    confirmIntent,
    onAuthenticated,
  });
  const authenticationFailed = phase === "authentication_failed";
  const reconciliationFailed = phase === "reconciliation_failed";

  return (
    <Stack spacing={1.5} role="status">
      {authenticationFailed ? (
        <Alert severity="error" role="alert">
          We couldn&apos;t confirm this payment. Try again or choose another
          card.
        </Alert>
      ) : reconciliationFailed ? (
        <Alert severity="error" role="alert">
          Your payment was confirmed, but we couldn&apos;t finish the request.
          Try again.
        </Alert>
      ) : phase === "reconciling" ? (
        <Typography>
          Your payment was confirmed. Finishing your request.
        </Typography>
      ) : phase === "complete" ? (
        <Typography>Payment confirmed.</Typography>
      ) : (
        <Typography>
          Confirm this payment with your bank to finish checkout.
        </Typography>
      )}
      <Box>
        <Button
          type="button"
          variant="contained"
          onClick={() => void authenticate()}
          disabled={!stripe || busy || phase === "complete"}
          aria-busy={busy || undefined}
        >
          {phase === "confirming"
            ? "Confirming…"
            : phase === "reconciling"
              ? "Finishing…"
              : authenticationFailed
                ? "Try again"
                : reconciliationFailed
                  ? "Finish request"
                  : phase === "complete"
                    ? "Payment confirmed"
                    : "Confirm payment"}
        </Button>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Terminal screens
// ---------------------------------------------------------------------------

function ConfirmedScreen({ entry }: { entry: GuardianConsentConfirmResponse }) {
  // Display the child by their resolved display name (never a uuid); fall back to
  // a neutral noun if the server didn't hydrate one. The minimal public projection
  // (#1699) carries player_display_name only — no raw player_name/uid.
  const childName = entry.player_display_name || null;
  const eventName = entry.event_name || null;
  // "paid" is the server's own truth that the off-session charge succeeded; a free
  // entry stays "not_required" and shows no money copy (no useless copy).
  const charged = entry.payment_status === "paid";

  return (
    <ConsentShell>
      <Typography component="h1" variant="h4">
        Consent confirmed
      </Typography>
      <Typography role="status">
        {childName ? `${childName} is registered` : "Your child is registered"}
        {eventName ? ` for ${eventName}` : ""}.
      </Typography>
      {charged ? (
        <Typography variant="body2">
          Your card was charged {formatMoney(entry.amount_paid, entry.currency)}{" "}
          for the entry fee.
        </Typography>
      ) : null}
      <Typography variant="body2" color="text.secondary">
        You don&apos;t need to do anything else — you can close this page.
      </Typography>
    </ConsentShell>
  );
}

function InvalidLinkScreen() {
  return (
    <ConsentShell>
      <Typography component="h1" variant="h4">
        This link is invalid or has expired
      </Typography>
      <Alert severity="error" role="alert">
        We couldn&apos;t use this consent link. It may have already been used or
        timed out for security.
      </Alert>
      <Typography variant="body2" color="text.secondary">
        Please ask the event organizer to send a new consent link to your email.
      </Typography>
    </ConsentShell>
  );
}

function RegistrationChangedScreen() {
  return (
    <ConsentShell>
      <Typography component="h1" variant="h4">
        Registration needs to be restarted
      </Typography>
      <Alert severity="error" role="alert">
        The tournament requirements changed after this consent link was sent.
      </Alert>
      <Typography variant="body2" color="text.secondary">
        Please ask the event organizer to restart the registration and send a
        new consent link.
      </Typography>
    </ConsentShell>
  );
}

function AlreadyConfirmedScreen() {
  return (
    <ConsentShell>
      <Typography component="h1" variant="h4">
        Consent already confirmed
      </Typography>
      <Typography role="status">
        This guardian consent has already been recorded.
      </Typography>
    </ConsentShell>
  );
}

// ---------------------------------------------------------------------------
// Chrome-less centered MUI shell — self-contained because the guardian may be
// logged out. It intentionally does not depend on the authenticated app shell.
// ---------------------------------------------------------------------------

function ConsentShell({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="main"
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        p: 2,
        bgcolor: "background.default",
      }}
    >
      <Paper
        variant="outlined"
        sx={{ width: "min(100%, 42rem)", p: { xs: 2.5, sm: 4 } }}
      >
        <Stack spacing={2.5}>{children}</Stack>
      </Paper>
    </Box>
  );
}
